const MS_PER_HOUR = 3_600_000;
const END_TIME_HOUR = 24;

export interface ComputeMovedCardTimesInput {
  predecessorEndMs: number | null;
  slotsBeforeGap: number;
  rawDurationMs: number;
  dayStartMs: number;
  isFirstPosition: boolean;
  originalFirstStartMs?: number;
}

export interface ComputeMovedCardTimesResult {
  startMs: number;
  endMs: number;
}

/**
 * Computes the moved card's start/end times after a same-day reorder.
 *
 * Rules:
 * - startDate = ceil(predecessor.endDate, 1h) + slotsBeforeGap * 1h
 * - endDate = startDate + visualDuration (ceil rawDuration to full hours)
 * - The `duration` field is NOT changed — only dates are adjusted
 * - Clamp startDate so card fits within day boundary, floored to hour
 * - Never place card before its predecessor
 * - If first position, use the day's original first start time
 */
export function computeMovedCardTimes(input: ComputeMovedCardTimesInput): ComputeMovedCardTimesResult {
  const { predecessorEndMs, slotsBeforeGap, rawDurationMs, dayStartMs, isFirstPosition, originalFirstStartMs } = input;

  let startMs: number;

  if (isFirstPosition) {
    startMs = originalFirstStartMs ?? dayStartMs;
  } else if (predecessorEndMs !== null) {
    startMs = Math.ceil(predecessorEndMs / MS_PER_HOUR) * MS_PER_HOUR + slotsBeforeGap * MS_PER_HOUR;
  } else {
    startMs = dayStartMs;
  }

  // Visual duration: ceil to full hours so endDate lands on the hour grid.
  // A 90-min card occupies 2 hour blocks visually, so endDate = start + 2h.
  const visualDurationMs = Math.ceil(rawDurationMs / MS_PER_HOUR) * MS_PER_HOUR;

  // Clamp: card must end by day boundary (00:00 next day).
  // Uses visual duration so the clamp accounts for the full visual block size.
  const dayEnd = new Date(dayStartMs);
  dayEnd.setUTCHours(END_TIME_HOUR, 0, 0, 0);
  const maxStartMs = Math.floor((dayEnd.getTime() - visualDurationMs) / MS_PER_HOUR) * MS_PER_HOUR;
  if (startMs > maxStartMs) {
    startMs = maxStartMs;
  }

  return {
    startMs,
    endMs: startMs + visualDurationMs,
  };
}
